import { useVSFContext } from '@vue-storefront/core';
import { sharedRef } from '@vue-storefront/core';
import { onMounted, computed, watch } from '@nuxtjs/composition-api';
import { BloomreachSegment, UseBloomreachSegmentsReturn } from './interfaces';
import { useCurrentUser } from '../useCurrentUser';

export const useBloomreachSegments = (): UseBloomreachSegmentsReturn => {
  const context = useVSFContext();
  const { currentUser } = useCurrentUser();
  const segments = sharedRef<BloomreachSegment[]>(
    [],
    'useBloomreachSegments-segments'
  );
  const ruleContexts = sharedRef<string[]>(
    [],
    'useBloomreachSegments-ruleContexts'
  );
  const isLoading = sharedRef<boolean>(false, 'useBloomreachSegments-loading');
  const error = sharedRef<string | null>(null, 'useBloomreachSegments-error');

  const BLOOMREACH_EXPONEA_ENDPOINT = `https://api.eu1.exponea.com/webxp/s/${context.$config.bloomreachTrackingToken}/managed-endpoints/${context.$config.bloomreachSegmentId}`;
  const AUTH_TOKEN = context.$config.bloomreachSegmentAuthToken;

  const getStoredSegments = (): BloomreachSegment[] | null => {
    try {
      const stored = sessionStorage.getItem('bloomreach_segments');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const setStoredSegments = (segments: BloomreachSegment[]): void => {
    try {
      sessionStorage.setItem('bloomreach_segments', JSON.stringify(segments));
    } catch (error) {
      console.error('Failed to store segments:', error);
    }
  };

  const loadSegments = async (force = false): Promise<void> => {
    if (!force && getStoredSegments()) {
      segments.value = getStoredSegments();
      ruleContexts.value = segments.value.map((s) => s.id);
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const exponea = (window as any).exponea;
      if (!exponea) throw new Error('Bloomreach SDK not available');

      const exponeaCookie = computed(
        () => context.$cookies.get('__exponea_etc__') || null
      );
      const customerEmail = exponea?.user?.email;
      const customerIds: Record<string, string> = {};

      if (customerEmail) {
        customerIds.email = customerEmail;
      } else if (exponeaCookie.value) {
        customerIds.cookie = exponeaCookie.value;
      } else {
        throw new Error(
          'No email or exponea cookie available for identification'
        );
      }

      const response = await fetch(BLOOMREACH_EXPONEA_ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${AUTH_TOKEN}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_ids: customerIds,
          params: {}
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch segments: ${response.status}`);
      }

      const segmentData = await response.json();

      const mappedSegments: BloomreachSegment[] = Object.entries(segmentData)
        .filter(([, value]) => value !== 'None')
        .map(([experimentName, assignedSegment]) => ({
          id: String(assignedSegment),
          name: experimentName.trim()
        }));

      const newRuleContexts = mappedSegments.map((s) => s.id);

      segments.value = mappedSegments;
      ruleContexts.value = newRuleContexts;
      setStoredSegments(mappedSegments);

      console.log('Loaded segments:', segments.value);
      console.log('Derived rule contexts:', ruleContexts.value);
    } catch (err) {
      console.error('Failed to load segments:', err);
      error.value = err instanceof Error ? err.message : String(err);
      ruleContexts.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  // session storage cache automatically handles persistence during a session
  // This function can however, be used to force a refresh of segments, e.g., after user updates their profile
  const refreshSegments = async (): Promise<void> => {
    await loadSegments(true);
  };

  watch(
    () => context.$exponea?.user?.email,
    (newEmail, oldEmail) => {
      if (newEmail !== oldEmail) {
        loadSegments(true);
      }
    }
  );

  watch(
    () => currentUser.value,
    (newUser, oldUser) => {
      if (!oldUser && newUser) {
        loadSegments(true);
      }
    }
  );

  onMounted(() => {
    loadSegments();
  });

  return {
    segments,
    ruleContexts,
    isLoading,
    error,
    loadSegments,
    refreshSegments
  };
};
